import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import api from '../api/api';
// 新しい基本テーマをインポート
import { basicThemes, generateThemeClasses } from './BasicThemes';
// スタンダードプラン時間帯テーマをインポート
import { standardThemes, generateStandardThemeClasses, setStandardThemeCSSVars, getStandardEffectClass } from './StandardThemes';

const ThemeContext = createContext();

// テーマ定義（新しい基本テーマ + スタンダードテーマ + 既存の季節・プレミアムテーマ）
export const themes = {
  // 新しい基本テーマ
  ...basicThemes,
  
  // スタンダードプラン時間帯テーマ
  ...standardThemes
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const { plan, user, isAuthenticated } = useAuth();
  
  // プラン別デフォルトテーマ
  const getDefaultTheme = () => {
    switch (plan) {
      case 'premium':
        return 'premiumExecutive'; // プレミアム専用テーマ
      case 'standard':
        return 'morningCalm'; // 時間帯テーマのデフォルト
      case 'free':
      default:
        return 'lightBlue'; // 基本テーマのデフォルト
    }
  };
  
  const [currentTheme, setCurrentTheme] = useState(getDefaultTheme());

  // プラン別で使用可能なテーマを取得
  const getAvailableThemes = () => {
    const basicThemes = Object.values(themes).filter(t => t.category === 'basic');
    const standardThemes = Object.values(themes).filter(t => t.category === 'standard');
    const premiumThemes = Object.values(themes).filter(t => t.category === 'premium');
    
    // フリープラン限定: タイムモーニングカームテーマを体験可能
    const freeTrialTheme = standardThemes.filter(t => t.id === 'morningCalm');

    switch (plan) {
      case 'premium':
        return [...basicThemes, ...standardThemes, ...premiumThemes];
      case 'standard':
        return [...basicThemes, ...standardThemes];
      case 'free':
      default:
        return [...basicThemes, ...freeTrialTheme];
    }
  };

  // アカウントにテーマを保存
  const saveThemeToAccount = async (themeId) => {
    if (!isAuthenticated || !user) return;
    
    try {
      await api.put('/api/profile/theme', { theme: themeId });
    } catch (error) {
      console.error('Failed to save theme to account:', error);
    }
  };

  // アカウントからテーマを読み込み
  const loadThemeFromAccount = async () => {
    if (!isAuthenticated || !user) return null;
    
    try {
      const response = await api.get('/api/profile/theme');
      if (response.data.success && response.data.theme) {
        return response.data.theme;
      }
    } catch (error) {
      console.error('Failed to load theme from account:', error);
    }
    return null;
  };

  // テーマ変更（アカウント連携対応）
  const changeTheme = async (themeId) => {
    const availableThemes = getAvailableThemes();
    const theme = availableThemes.find(t => t.id === themeId);
    
    if (theme) {
      setCurrentTheme(themeId);
      
      // ローカルストレージにも保存（オフライン対応）
      localStorage.setItem('selectedTheme', themeId);
      
      // アカウントにも保存（ログイン時）
      if (isAuthenticated) {
        await saveThemeToAccount(themeId);
      }
    } else {
    }
  };

  // テーマ初期化処理
  useEffect(() => {
    const initializeTheme = async () => {
      let themeToSet = getDefaultTheme(); // フォールバック
      
      if (isAuthenticated) {
        // 1. アカウントからテーマを読み込み（最優先）
        const accountTheme = await loadThemeFromAccount();
        if (accountTheme) {
          const availableThemes = getAvailableThemes();
          const theme = availableThemes.find(t => t.id === accountTheme);
          if (theme) {
            themeToSet = accountTheme;
          }
        }
      } else {
        // 2. ローカルストレージからテーマを読み込み（未ログイン時）
        const savedTheme = localStorage.getItem('selectedTheme');
        if (savedTheme) {
          const availableThemes = getAvailableThemes();
          const theme = availableThemes.find(t => t.id === savedTheme);
          if (theme) {
            themeToSet = savedTheme;
          }
        }
      }
      
      setCurrentTheme(themeToSet);
    };

    initializeTheme();
  }, [plan, isAuthenticated, user]); // user追加で認証状態変化に対応

  // プラン変更時のデフォルトテーマ更新処理
  useEffect(() => {
    // プラン変更時は現在のテーマが利用可能かチェック
    const availableThemes = getAvailableThemes();
    const currentThemeAvailable = availableThemes.find(t => t.id === currentTheme);
    
    if (!currentThemeAvailable) {
      // 現在のテーマが利用不可になった場合、新プランのデフォルトに変更
      const newDefaultTheme = getDefaultTheme();
      setCurrentTheme(newDefaultTheme);
      
      // ローカルストレージとアカウントも更新
      localStorage.setItem('selectedTheme', newDefaultTheme);
      if (isAuthenticated) {
        saveThemeToAccount(newDefaultTheme);
      }
    }
  }, [plan]); // planのみに依存

  // CSS変数を設定
  useEffect(() => {
    const theme = themes[currentTheme];
    if (theme) {
      const root = document.documentElement;
      Object.entries(theme.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
      
      // スタンダードテーマ（プレミアム含む）の場合、専用CSS変数を設定
      if (standardThemes[currentTheme]) {
        setStandardThemeCSSVars(theme);
      }
      
      
      // テーマに応じてbody classを追加
      const body = document.body;
      body.classList.remove(
        'goldenMist', 'standard-theme',
        'standard-morning-theme', 'standard-midday-theme', 'standard-evening-theme', 'standard-night-theme',
        'standard-relax-theme', 'standard-energy-theme', 'standard-focus-theme', 'standard-balance-theme',
        'premium-executive-theme', 'premium-royal-theme', 'premium-platinum-theme', 'premium-luxury-theme'
      );
      
      if (currentTheme === 'goldenMist') {
        body.classList.add('goldenMist');
      }
      
      // スタンダードテーマ（プレミアム含む）の場合、専用クラスとテーマ効果を追加
      if (standardThemes[currentTheme]) {
        body.classList.add('standard-theme', getStandardEffectClass(currentTheme));
      }
      
    }
  }, [currentTheme]);

  // プレミアム効果クラス生成
  const getPremiumEffectClasses = (themeId) => {
    switch (themeId) {
      case 'lavenderCalm':
        return 'seasonal-lavender-float premium-lavender-sparkle';
      case 'sakuraMist':
        return 'seasonal-sakura-float premium-mist-glow';
      case 'oceanBreeze':
        return 'seasonal-ocean-wave premium-bubble-float';
      case 'forestHarmony':
        return 'seasonal-forest-sway premium-nature-glow';
      case 'goldenMist':
        return 'premium-luxury-glow premium-elegant-shimmer';
      default:
        return '';
    }
  };

  // 季節効果クラス生成
  const getSeasonalEffectClasses = (themeId) => {
    switch (themeId) {
      case 'spring':
        return 'seasonal-spring-float';
      case 'summer':
        return 'seasonal-summer-wave';
      case 'autumn':
        return 'seasonal-autumn-sway';
      case 'winter':
        return 'seasonal-winter-glow seasonal-winter-shimmer';
      case 'winterLandscape':
        return 'seasonal-winter-snow';
      default:
        return '';
    }
  };

  // 背景パターン取得
  const getBackgroundPattern = (themeId) => {
    switch (themeId) {
      case 'spring':
        return 'pattern-spring';
      case 'summer':
        return 'pattern-summer';
      case 'autumn':
        return 'pattern-autumn';
      case 'winter':
        return 'pattern-winter';
      case 'winterLandscape':
        return 'pattern-winter-landscape';
      case 'lavenderCalm':
        return 'pattern-lavender';
      case 'sakuraMist':
        return 'pattern-sakura-mist';
      case 'oceanBreeze':
        return 'pattern-ocean-waves';
      case 'forestHarmony':
        return 'pattern-forest-canopy';
      case 'goldenMist':
        return 'pattern-golden-luxury';
      default:
        const theme = themes[themeId];
        return theme ? `bg-${theme.colors.background}` : 'bg-gray-50';
    }
  };

  // テーマに基づくTailwindクラス生成ヘルパー
  const getThemeClasses = () => {
    const theme = themes[currentTheme];
    if (!theme) {
      return {};
    }
    
    
    // 新しい基本テーマの場合は新システムを使用
    if (basicThemes[currentTheme]) {
      return generateThemeClasses(theme);
    }
    
    // スタンダードテーマの場合は専用システムを使用
    if (standardThemes[currentTheme]) {
      return generateStandardThemeClasses(theme);
    }
    
    
    // 既存テーマ（季節・プレミアム）の場合は既存ロジックを使用
    const isGradientBackground = theme.colors.background?.includes('gradient');
    
    return {
      // ボタンスタイル
      primaryButton: currentTheme === 'goldenMist'
        ? `bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 hover:from-amber-700 hover:via-yellow-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-2xl transform hover:-translate-y-0.5 transition-all duration-200 border-2 border-amber-800/30 hover:border-amber-800/50`
        : currentTheme === 'lavenderCalm'
        ? `bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200`
        : theme.category === 'premium' 
        ? `premium-theme-button text-white font-semibold` 
        : `bg-${theme.colors.primary}-600 hover:bg-${theme.colors.primary}-700 text-white`,
      secondaryButton: currentTheme === 'goldenMist'
        ? `bg-gradient-to-r from-amber-100 to-yellow-100 hover:from-amber-200 hover:to-yellow-200 text-amber-800 border-2 border-amber-300 hover:border-amber-400 font-semibold shadow-lg hover:shadow-xl transition-all duration-200`
        : currentTheme === 'lavenderCalm'
        ? `bg-gradient-to-r from-purple-100 to-violet-100 hover:from-purple-200 hover:to-violet-200 text-purple-800 border border-purple-200`
        : `bg-${theme.colors.secondary}-100 hover:bg-${theme.colors.secondary}-200 text-${theme.colors.secondary}-800`,
      
      // カードスタイル  
      card: currentTheme === 'lavenderCalm'
        ? `bg-violet-25 backdrop-blur-sm border border-violet-200`
        : theme.category === 'premium' 
        ? `bg-white/90 backdrop-blur-lg border border-${theme.colors.primary}-200 premium-card-shadow`
        : theme.category === 'seasonal'
        ? currentTheme === 'winter'
          ? `bg-white/85 backdrop-blur-sm border border-${theme.colors.primary}-200` // 冬テーマは少し透明に
          : `bg-white/95 backdrop-blur-sm border border-${theme.colors.primary}-200`
        : `bg-${theme.colors.cardBg} border border-${theme.colors.primary}-200`,
      cardHeader: currentTheme === 'lavenderCalm'
        ? `bg-violet-50 border-b border-violet-200`
        : theme.category === 'premium'
        ? `bg-gradient-to-r from-${theme.colors.primary}-50 to-${theme.colors.secondary}-50 border-b border-${theme.colors.primary}-200`
        : `bg-${theme.colors.primary}-50 border-b border-${theme.colors.primary}-200`,
      
      // 背景（パターン付き）
      pageBackground: isGradientBackground 
        ? `bg-${theme.colors.background}` 
        : getBackgroundPattern(currentTheme),
      
      // ヘッダー・フッター（プレミアムテーマはグラデーション）
      header: theme.colors.headerBg?.includes('gradient')
        ? `bg-${theme.colors.headerBg} text-${theme.colors.headerText}`
        : `bg-${theme.colors.headerBg} text-${theme.colors.headerText}`,
      footer: theme.colors.footerBg?.includes('gradient')
        ? `bg-${theme.colors.footerBg} text-${theme.colors.footerText}`
        : `bg-${theme.colors.footerBg} text-${theme.colors.footerText}`,
      
      // テキスト（プレミアムテーマは特別なテキストエフェクト）
      textPrimary: `text-${theme.colors.textPrimary}`,
      textSecondary: `text-${theme.colors.textSecondary}`,
      textAccent: theme.category === 'premium' 
        ? getPremiumTextClass(currentTheme) 
        : `text-${theme.colors.primary}-600`,
      headerText: `text-${theme.colors.headerText}`,
      footerText: `text-${theme.colors.footerText}`,
      footerLink: `text-${theme.colors.footerLink}`,
      
      // アクセント
      accentBorder: `border-${theme.colors.primary}-300`,
      accentBg: `bg-${theme.colors.primary}-50`,
      
      // フォーカス
      focusRing: `focus:ring-2 focus:ring-${theme.colors.primary}-500 focus:border-transparent`,
      
      // グラデーション（装飾用）
      gradient: `bg-gradient-to-r ${theme.decorations?.gradients?.join(' ') || 'from-gray-100 to-gray-200'}`,
      
      // 装飾
      headerIcon: theme.decorations?.headerIcon || '🏠',
      footerPattern: theme.decorations?.footerPattern || 'subtle',
      
      // エフェクト
      premiumEffect: currentTheme === 'lavenderCalm' 
        ? getPremiumEffectClasses(currentTheme) 
        : theme.category === 'premium' 
        ? getPremiumEffectClasses(currentTheme) 
        : '',
      seasonalEffect: theme.category === 'seasonal' 
        ? getSeasonalEffectClasses(currentTheme) 
        : currentTheme === 'lavenderCalm'
        ? 'seasonal-lavender-float'
        : '',
      
      // プレミアム専用プロパティ
      isPremium: theme.category === 'premium',
      hasSvgIcons: theme.decorations?.svgIcons || false,
      hasFontAwesome: theme.decorations?.fontAwesome || false,
      iconTheme: theme.decorations?.iconTheme || 'default'
    };
  };

  // プレミアムテーマ別テキストクラス取得
  const getPremiumTextClass = (themeId) => {
    switch (themeId) {
      case 'lavenderCalm':
        return 'text-violet-700';
      case 'sakuraMist':
        return 'text-rose-700';
      case 'oceanBreeze':
        return 'text-sky-700';
      case 'forestHarmony':
        return 'text-emerald-700';
      case 'goldenMist':
        return 'text-amber-800';
      default:
        return 'text-current';
    }
  };

  const value = {
    currentTheme,
    currentThemeData: themes[currentTheme],
    availableThemes: getAvailableThemes(),
    changeTheme,
    getThemeClasses,
    getPremiumTextClass,
    themes
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;